import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ShieldCheck, X } from 'lucide-react';

/**
 * RGPD Cookie Consent Banner — shown once, stored in localStorage.
 */
export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('nannyou_cookie_consent');
    if (!consent) {
      // Small delay so it doesn't flash on page load
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('nannyou_cookie_consent', 'accepted');
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('nannyou_cookie_consent', 'declined');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[400] p-4 animate-in slide-in-from-bottom duration-300"
      data-testid="cookie-consent-banner"
    >
      <div
        className="max-w-2xl mx-auto rounded-xl border shadow-2xl px-5 py-4 backdrop-blur-md"
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderColor: 'var(--neutral-200)',
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5"
            style={{ backgroundColor: 'var(--primary-50)' }}
          >
            <ShieldCheck className="h-4.5 w-4.5" style={{ color: 'var(--primary-400)' }} />
          </div>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
              Cookies & confidentialité
            </p>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Nannyou utilise des cookies strictement nécessaires au fonctionnement du service
              (authentification, préférences). Nous ne collectons aucune donnée à des fins
              publicitaires. En poursuivant, vous acceptez notre{' '}
              <a
                href="/legal"
                className="underline"
                style={{ color: 'var(--primary-400)' }}
                onClick={(e) => { e.preventDefault(); window.open('/legal', '_blank'); }}
              >
                politique de confidentialité
              </a>.
            </p>

            <div className="flex items-center gap-2 mt-3">
              <Button
                size="sm"
                className="text-white text-xs h-8 px-4"
                style={{ backgroundColor: 'var(--primary-400)' }}
                onClick={handleAccept}
                data-testid="cookie-accept-btn"
              >
                Accepter
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-xs h-8 px-3"
                style={{ color: 'var(--text-secondary)' }}
                onClick={handleDecline}
                data-testid="cookie-decline-btn"
              >
                Refuser les non-essentiels
              </Button>
            </div>
          </div>

          <button
            onClick={handleDecline}
            className="shrink-0 p-1 rounded-md hover:bg-[var(--neutral-100)] transition-colors"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>
      </div>
    </div>
  );
};
